import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { defaultCommunityTemplate } from '../data/communityTemplate';
import apiService from '../services/api';

export function CommunityWizard({ user, onComplete }) {
  const [step, setStep] = useState(1);
  const [wizardData, setWizardData] = useState({
    name: '',
    description: '',
    slug: '',
    template: defaultCommunityTemplate,
  });
  const [slugLocked, setSlugLocked] = useState(false);
  const [isDeploying, setIsDeploying] = useState(false);
  const [deployError, setDeployError] = useState(null);

  const toSlug = useCallback((value = '') => (
    value
      .toString()
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)+/g, '')
  ), []);

  const handleBlueprintChange = useCallback((updatedCommunity) => {
    setWizardData((prev) => ({ ...prev, template: updatedCommunity }));
  }, []);

  useEffect(() => {
    if (wizardData.name && wizardData.name.trim() && !slugLocked) {
      const nextSlug = toSlug(wizardData.name);
      setWizardData((prev) => (prev.slug === nextSlug ? prev : {
        ...prev,
        slug: nextSlug,
      }));
    }
  }, [wizardData.name, slugLocked, toSlug]);

  useEffect(() => {
    if (!wizardData.slug) {
      setSlugLocked(false);
    }
  }, [wizardData.slug]);

  const handleNext = () => {
    if (step === 1 && !wizardData.name.trim()) return;
    setStep((prev) => Math.min(prev + 1, 3));
  };

  const handleBack = () => setStep((prev) => Math.max(prev - 1, 1));

  const handleSlugChange = useCallback((event) => {
    const value = event.target.value;
    if (!value.trim()) {
      setSlugLocked(false);
      setWizardData((prev) => ({
        ...prev,
        slug: '',
      }));
      return;
    }

    setSlugLocked(true);
    setWizardData((prev) => ({
      ...prev,
      slug: toSlug(value),
    }));
  }, [toSlug]);

  const handleLaunch = async () => {
    setIsDeploying(true);
    setDeployError(null);
    try {
      const name = wizardData.name.trim();
      const description = wizardData.description?.trim() || '';
      const slugCandidate = wizardData.slug?.trim();

      const communityRes = await apiService.createCommunity({
        name,
        description,
        slug: slugCandidate ? toSlug(slugCandidate) : undefined,
        creator_id: user?.id,
      });

      const communityId = communityRes?.id;
      if (!communityId) {
        throw new Error('Community creation did not return an ID.');
      }

      const channels = [];
      (wizardData.template?.tiers || []).forEach((tier, tIdx) => {
        (tier.channels || []).forEach((chan, cIdx) => {
          if (chan.enabled === false) return;
          channels.push({
            id: chan.blueprintId || chan.id,
            blueprintId: chan.blueprintId || chan.id,
            name: chan.name,
            topic: chan.topic,
            emoji: chan.emoji,
            type: chan.type,
            tierId: tier.id,
            tierLabel: tier.label,
            tierVisibility: tier.visibility,
            visibility: chan.visibility || tier.visibility,
            priority: chan.priority ?? tIdx * 10 + cIdx,
            enabled: chan.enabled !== false,
            metadata: chan.metadata || {},
            bots: chan.bots || chan.metadata?.bots || [],
          });
        });
      });

      await apiService.scaffoldCommunityChannels(communityId, {
        blueprintVersion: wizardData.template?.layout_version || 'wizard-v1',
        channels,
      });

      if (typeof onComplete === 'function') {
        onComplete(communityRes);
      } else {
        const targetSlug = communityRes?.slug || (slugCandidate ? toSlug(slugCandidate) : '') || toSlug(name) || communityId;
        window.location.href = `/communities/${targetSlug}`;
      }
    } catch (err) {
      console.error('Wizard Launch Failed', err);
      setDeployError(err.message || 'Failed to launch community.');
    } finally {
      setIsDeploying(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 text-white flex flex-col items-center py-12 px-4">
      <div className="w-full max-w-4xl mb-8">
        <div className="flex items-center justify-between text-sm text-slate-400 mb-4 uppercase tracking-widest">
          <span className={step >= 1 ? 'text-emerald-400 font-bold' : ''}>1. Identity</span>
          <span className="h-px bg-white/10 flex-1 mx-4" />
          <span className={step >= 2 ? 'text-emerald-400 font-bold' : ''}>2. Channels & Roles</span>
          <span className="h-px bg-white/10 flex-1 mx-4" />
          <span className={step >= 3 ? 'text-emerald-400 font-bold' : ''}>3. Launch</span>
        </div>
      </div>

      <div className="w-full max-w-5xl">
        {step === 1 && (
          <div className="bg-white/5 border border-white/10 rounded-3xl p-8 max-w-2xl mx-auto animate-in fade-in slide-in-from-bottom-4">
            <h2 className="text-3xl font-black mb-2">Name your Community</h2>
            <p className="text-slate-400 mb-8">This is the public face of your server. You can change this later.</p>

            <div className="space-y-6">
              <label className="block">
                <span className="text-sm font-semibold text-slate-300">Community Name</span>
                <input
                  type="text"
                  value={wizardData.name}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (!value.trim()) {
                      setSlugLocked(false);
                    }
                    setWizardData((prev) => ({ ...prev, name: value }));
                  }}
                  className="mt-2 w-full bg-black/40 border border-white/20 rounded-xl px-4 py-3 text-lg focus:border-emerald-500 focus:outline-none transition-colors"
                  placeholder="e.g. The Architects, Beta Squad..."
                  autoFocus
                />
              </label>

              <label className="block">
                <span className="text-sm font-semibold text-slate-300">Description (Optional)</span>
                <textarea
                  value={wizardData.description}
                  onChange={(e) => setWizardData((prev) => ({ ...prev, description: e.target.value }))}
                  className="mt-2 w-full bg-black/40 border border-white/20 rounded-xl px-4 py-3 focus:border-emerald-500 focus:outline-none transition-colors"
                  rows={3}
                  placeholder="What is this space for?"
                />
              </label>

              <label className="block">
                <span className="text-sm font-semibold text-slate-300">Custom URL slug (Optional)</span>
                <input
                  type="text"
                  value={wizardData.slug}
                  onChange={handleSlugChange}
                  className="mt-2 w-full bg-black/40 border border-white/20 rounded-xl px-4 py-3 focus:border-emerald-500 focus:outline-none transition-colors"
                  placeholder="e.g. architects"
                />
                <div className="flex items-center justify-between mt-2 text-xs text-slate-400">
                  <span>
                    Preview:&nbsp;
                    <span className="text-white/80">
                      /communities/{wizardData.slug || toSlug(wizardData.name || 'your-community')}
                    </span>
                  </span>
                  {slugLocked && wizardData.slug && (
                    <button
                      type="button"
                      onClick={() => {
                        setSlugLocked(false);
                        setWizardData((prev) => ({
                          ...prev,
                          slug: toSlug(prev.name || ''),
                        }));
                      }}
                      className="text-emerald-400 hover:text-emerald-300"
                    >
                      Reset
                    </button>
                  )}
                </div>
              </label>
            </div>

            <div className="mt-8 flex justify-end">
              <button
                onClick={handleNext}
                disabled={!wizardData.name.trim()}
                className="bg-emerald-500 hover:bg-emerald-400 disabled:opacity-50 disabled:cursor-not-allowed text-black font-bold py-3 px-8 rounded-full transition-all"
              >
                Next: Setup Channels →
              </button>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="animate-in fade-in zoom-in-95">
            <div className="flex justify-between items-end mb-6">
              <div>
                <h2 className="text-2xl font-bold">Configure your Space</h2>
                <p className="text-slate-400">
                  Add channels, set permissions, and configure bots.
                  <br />
                  This layout will be applied to your profile.
                </p>
              </div>
              <div className="flex gap-3">
                <button onClick={handleBack} className="px-4 py-2 text-slate-400 hover:text-white">
                  Back
                </button>
                <button
                  onClick={handleNext}
                  className="bg-emerald-500 hover:bg-emerald-400 text-black font-bold py-2 px-6 rounded-full"
                >
                  Review & Launch →
                </button>
              </div>
            </div>

            <CommunityBlueprint
              community={wizardData.template}
              sandboxMode
              canCustomize
              onChange={handleBlueprintChange}
            />
          </div>
        )}

        {step === 3 && (
          <div className="bg-white/5 border border-white/10 rounded-3xl p-8 max-w-2xl mx-auto text-center animate-in fade-in slide-in-from-bottom-4">
            <div className="text-6xl mb-6">🚀</div>
            <h2 className="text-3xl font-black mb-4">Ready for Liftoff?</h2>
            <p className="text-slate-300 mb-8">
              You are about to create <strong>{wizardData.name}</strong> with the custom channel configuration you just built.
            </p>

            {deployError && (
              <div className="bg-red-500/10 border border-red-500/50 text-red-200 p-4 rounded-xl mb-6 text-sm">
                {deployError}
              </div>
            )}

            <div className="flex justify-center gap-4">
              <button
                onClick={handleBack}
                disabled={isDeploying}
                className="px-6 py-3 rounded-full border border-white/10 hover:bg-white/5"
              >
                Go Back
              </button>
              <button
                onClick={handleLaunch}
                disabled={isDeploying}
                className="bg-emerald-500 hover:bg-emerald-400 text-black font-bold py-3 px-10 rounded-full flex items-center gap-2"
              >
                {isDeploying ? 'Deploying Server...' : 'Launch Community'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const previewModes = [
  { id: 'viewer', label: 'Public Viewer' },
  { id: 'follower', label: 'Follower' },
  { id: 'friend', label: 'Friend / Collab' },
  { id: 'donor', label: 'Donor / Subscriber' },
  { id: 'moderator', label: 'Moderator' },
  { id: 'staff', label: 'Staff' },
  { id: 'owner', label: 'Creator / Owner' },
];

const visibilityMatrix = {
  viewer: ['public'],
  follower: ['public', 'followers'],
  friend: ['public', 'followers', 'friends'],
  donor: ['public', 'followers', 'donors'],
  moderator: ['public', 'followers', 'friends', 'donors', 'moderators'],
  staff: ['public', 'followers', 'friends', 'donors', 'moderators', 'staff'],
  owner: 'all',
};

const tierToPreviewMap = {
  public: 'viewer',
  followers: 'follower',
  friends: 'friend',
  donors: 'donor',
  moderators: 'moderator',
  staff: 'staff',
  owner: 'owner',
};

const channelTypeBadge = {
  text: { label: 'Text', classes: 'bg-white/10 text-white' },
  voice: { label: 'Voice', classes: 'bg-emerald-500/20 text-emerald-200' },
  announcement: { label: 'Broadcast', classes: 'bg-sky-500/20 text-sky-200' },
};

const channelTypeOptions = [
  { id: 'text', label: 'Text room', description: 'Posts, threads, async planning', emoji: '💬' },
  { id: 'voice', label: 'Voice / Stage', description: 'Live audio, AMAs, war rooms', emoji: '🎙️' },
  { id: 'announcement', label: 'Broadcast', description: 'Read-only alerts and drops', emoji: '📣' },
];

const botTriggerOptions = [
  { id: 'on-join', label: 'On member join' },
  { id: 'scheduled', label: 'On schedule' },
  { id: 'command', label: 'Slash command' },
];

const createEmptyBot = () => ({
  id: typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : `bot-${Date.now()}`,
  name: '',
  trigger: 'on-join',
  endpoint: '',
  instructions: '',
});

const getDefaultEmojiForType = (type = 'text') =>
  channelTypeOptions.find((option) => option.id === type)?.emoji || '💬';

const buildChannelFormState = (channel = null, fallbackTier = 'public', fallbackVisibility = 'public') => {
  const channelType = channel?.type || channel?.channel_type || 'text';
  const botSource = channel?.bots || channel?.metadata?.bots || [];
  const existingBots = Array.isArray(botSource)
    ? botSource.map((bot, index) => ({
        id: bot.id || `${channel?.id || 'bot'}-${index}`,
        name: bot.name || '',
        trigger: bot.trigger || 'on-join',
        endpoint: bot.endpoint || '',
        instructions: bot.instructions || '',
      }))
    : [];
  return {
    id: channel?.id ?? null,
    name: channel?.name || '',
    slug: channel?.slug || '',
    channel_type: channelType,
    tier: channel?.tier || fallbackTier,
    visibility: channel?.visibility || fallbackVisibility || fallbackTier,
    emoji: channel?.emoji || getDefaultEmojiForType(channelType) || '💬',
    topic: channel?.topic || channel?.description || '',
    metadata: channel?.metadata || {},
    is_default: Boolean(channel?.is_default),
    is_welcome: Boolean(channel?.is_welcome),
    welcome_message: channel?.welcome_message || '',
    bots: existingBots,
  };
};

const templateTierMap = defaultCommunityTemplate.tiers.reduce((map, tier) => {
  map[tier.id] = tier;
  return map;
}, {});

const normalizeCommunity = (community) => {
  if (!community || !Array.isArray(community.tiers) || !community.tiers.length) {
    return defaultCommunityTemplate;
  }

  return {
    id: community.id || defaultCommunityTemplate.id,
    name: community.name || defaultCommunityTemplate.name,
    description: community.description || defaultCommunityTemplate.description,
    tiers: community.tiers.map((tier) => {
      const refTier = templateTierMap[tier.id] || templateTierMap[tier.visibility] || {};
      const requirementLabel = tier.requirementLabel || tier.requirement_label || refTier.requirementLabel;
      return {
        id: tier.id || tier.visibility || refTier.id,
        label: tier.label || refTier.label || tier.id,
        visibility: tier.visibility || refTier.visibility || tier.id,
        description: tier.description || refTier.description,
        requirementLabel: requirementLabel || 'Access limited',
        badgeColor: tier.badgeColor || tier.badge_color || refTier.badgeColor || 'from-slate-600 to-slate-800',
        locked: tier.locked ?? false,
        channels: (tier.channels || refTier.channels || []).map((channel) => ({
          id: channel.id ?? channel.name ?? `${tier.id}-channel`,
          name: channel.name || channel.channel_name || 'channel',
          slug: channel.slug || null,
          emoji: channel.emoji || refTier.channels?.find((c) => c.id === channel.id)?.emoji || '💬',
          type: channel.type || channel.channel_type || 'text',
          topic: channel.topic || channel.description || 'Conversation starter',
          visibility: channel.visibility || tier.visibility || 'public',
          isDefault: channel.is_default || channel.isDefault,
          tier: channel.tier || tier.visibility || tier.id,
          metadata: channel.metadata || {},
          is_welcome: channel.is_welcome,
          welcome_message: channel.welcome_message || '',
          bots: channel.bots || channel.metadata?.bots || [],
        })),
      };
    }),
  };
};

const buildChannelMap = (template, overrides = {}) => {
  const map = {};
  template.tiers.forEach((tier, tierIndex) => {
    tier.channels.forEach((channel, channelIndex) => {
      const override = overrides[channel.id] || {};
      map[channel.id] = {
        ...channel,
        enabled: override.enabled !== undefined ? override.enabled : true,
        priority: override.priority ?? tierIndex * 10 + channelIndex + 1,
        tierId: tier.id,
        tierVisibility: tier.visibility,
        blueprintId: channel.id,
        ipNamespace: override.ipNamespace || `aiia.community.${tier.id}`
      };
    });
  });
  return map;
};

const deriveInitialPreview = (community, explicitPreview) => {
  if (explicitPreview) return explicitPreview;
  const access = community?.access || {};
  if (access.viewer_role === 'owner') return 'owner';
  if (access.viewer_role === 'moderator') return 'moderator';
  if (access.viewer_role === 'staff') return 'staff';
  const tierPreview = tierToPreviewMap[access.viewer_tier];
  if (tierPreview) return tierPreview;
  return 'viewer';
};

const normalizeAllowedVisibilities = (allowed) => {
  if (!allowed) return ['public'];
  if (allowed === 'all') return 'all';
  if (Array.isArray(allowed)) {
    return allowed.length ? allowed : ['public'];
  }
  if (typeof allowed === 'string') {
    return [allowed];
  }
  return ['public'];
};

const deriveCustomizePermission = (community, override) => {
  if (typeof override === 'boolean') return override;
  const role = community?.access?.viewer_role;
  const tier = community?.access?.viewer_tier;
  return role === 'owner' || tier === 'staff';
};

const useMediaQuery = (query) => {
  const getMatches = () => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia(query).matches;
  };

  const [value, setValue] = useState(getMatches);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const mediaQuery = window.matchMedia(query);
    const handler = (event) => setValue(event.matches);
    setValue(mediaQuery.matches);
    mediaQuery.addEventListener('change', handler);
    return () => mediaQuery.removeEventListener('change', handler);
  }, [query]);

  return value;
};

export function CommunityBlueprint({
  community,
  initialPreview,
  canCustomize,
  onChannelSelect,
  compact = false,
  selectedChannelId = null,
  onScaffoldComplete = () => {},
  channelsLoading = false,
  onChannelMutation = () => {},
  onIngressRequest = null,
  sandboxMode = false,
  scaffoldCommunityId = null,
  scaffoldDisabledReason = null,
  onChange = null,
}) {
  const normalizedCommunity = useMemo(() => normalizeCommunity(community), [community]);
  const backendAllowedVisibilities = useMemo(
    () => normalizeAllowedVisibilities(community?.access?.allowed_visibilities),
    [community?.access?.allowed_visibilities]
  );
  const [sandboxCommunity, setSandboxCommunity] = useState(() => normalizedCommunity);
  useEffect(() => {
    if (sandboxMode) {
      setSandboxCommunity(normalizedCommunity);
    }
  }, [sandboxMode, normalizedCommunity]);
  const blueprintCommunity = sandboxMode ? sandboxCommunity : normalizedCommunity;
  const tierOptions = useMemo(
    () =>
      blueprintCommunity.tiers.map((tier) => ({
        id: tier.id,
        label: tier.label,
        visibility: tier.visibility || tier.id,
      })),
    [blueprintCommunity]
  );
  const resolvedPreview = useMemo(
    () => deriveInitialPreview(community, initialPreview),
    [community, initialPreview]
  );
  const [previewAs, setPreviewAs] = useState(resolvedPreview);
  const [customizeMode, setCustomizeMode] = useState(sandboxMode || false);
  useEffect(() => {
    if (sandboxMode) {
      setCustomizeMode(true);
    }
  }, [sandboxMode]);
  const editable = sandboxMode || deriveCustomizePermission(community, canCustomize);
  const [layoutOverrides, setLayoutOverrides] = useState({});
  const [channelConfigs, setChannelConfigs] = useState(() => buildChannelMap(blueprintCommunity));
  const [layoutMeta, setLayoutMeta] = useState({ usesCustomLayout: community?.uses_custom_layout ?? false, updatedAt: null });
  const [layoutLoading, setLayoutLoading] = useState(false);
  const [layoutError, setLayoutError] = useState(null);
  const [isSavingLayout, setIsSavingLayout] = useState(false);
  const [isScaffolding, setIsScaffolding] = useState(false);
  const [scaffoldResult, setScaffoldResult] = useState(null);
  const defaultComposerTier = blueprintCommunity.tiers?.[0] || { id: 'public', label: 'Public Plaza', visibility: 'public' };
  const [channelComposerOpen, setChannelComposerOpen] = useState(false);
  const [channelComposerTier, setChannelComposerTier] = useState(defaultComposerTier);
  const [channelComposerData, setChannelComposerData] = useState(() =>
    buildChannelFormState(null, defaultComposerTier.id, defaultComposerTier.visibility)
  );
  const [channelComposerError, setChannelComposerError] = useState(null);
  const [composerBlueprintSource, setComposerBlueprintSource] = useState(null);
  const [isSavingChannel, setIsSavingChannel] = useState(false);
  const [isDeletingChannel, setIsDeletingChannel] = useState(false);
  const [editingChannel, setEditingChannel] = useState(null);
  useEffect(() => {
    if (!sandboxMode || typeof onChange !== 'function') return;
    onChange(sandboxCommunity);
  }, [sandboxCommunity, sandboxMode, onChange]);

  const targetCommunityId = scaffoldCommunityId || community?.id;
  const buildScaffoldPayload = useCallback(() => {
    const channels = [];
    blueprintCommunity.tiers.forEach((tier, tierIndex) => {
      tier.channels.forEach((channel, channelIndex) => {
        const config = channelConfigs[channel.id] || channel;
        channels.push({
          id: channel.id,
          blueprintId: channel.id,
          name: config.name || channel.name,
          topic: config.topic || channel.topic,
          emoji: config.emoji || channel.emoji || '💬',
          type: config.type || channel.type,
          tierId: tier.id,
          tierLabel: tier.label,
          tierVisibility: tier.visibility,
          priority: config.priority ?? tierIndex * 10 + channelIndex + 1,
          enabled: config.enabled !== false,
          ipNamespace: config.ipNamespace || `aiia.community.${tier.id}`,
          metadata: config.metadata || {}
        });
      });
    });
    return {
      blueprintVersion: community?.layout_version || '2025.11-demo',
      channels
    };
  }, [channelConfigs, blueprintCommunity, community?.layout_version]);

  const handleScaffoldChannels = useCallback(async () => {
    if (!targetCommunityId) {
      setLayoutError(scaffoldDisabledReason || 'Sign in or create a community to apply this blueprint');
      return;
    }
    setIsScaffolding(true);
    setLayoutError(null);
    try {
      const payload = buildScaffoldPayload();
  const response = await apiService.scaffoldCommunityChannels(targetCommunityId, payload);
      setScaffoldResult(response);
      onScaffoldComplete?.(response);
    } catch (error) {
      console.error('Community scaffold failed:', error);
      setLayoutError(error.message || 'Unable to deploy blueprint');
    } finally {
      setIsScaffolding(false);
    }
  }, [buildScaffoldPayload, onScaffoldComplete, scaffoldDisabledReason, targetCommunityId]);

  const openChannelComposer = (tier, channel = null, options = {}) => {
    if (!editable) return;
    const { forceCreate = false, blueprintSource = null } = options;
    const fallbackTierId = channel?.tier || tier?.id || 'public';
    const fallbackVisibility = channel?.visibility || tier?.visibility || fallbackTierId;
    const label = tier?.label || tierOptions.find((option) => option.id === fallbackTierId)?.label || fallbackTierId;
    const prepared = buildChannelFormState(channel, fallbackTierId, fallbackVisibility);
    if (!channel && !prepared.topic && tier?.requirementLabel) {
      prepared.topic = `Welcome to ${label}`;
    }
    if (!prepared.emoji) {
      prepared.emoji = getDefaultEmojiForType(prepared.channel_type);
    }
    setChannelComposerTier({ id: fallbackTierId, label, visibility: fallbackVisibility });
    setChannelComposerData(prepared);
    setComposerBlueprintSource(blueprintSource);
    setEditingChannel(!forceCreate && channel?.id ? channel : null);
    setChannelComposerError(null);
    setChannelComposerOpen(true);
  };

  const closeChannelComposer = () => {
    setChannelComposerOpen(false);
    setEditingChannel(null);
    setChannelComposerError(null);
    setComposerBlueprintSource(null);
    setChannelComposerData((prev) =>
      buildChannelFormState(null, channelComposerTier?.id || prev.tier || 'public', channelComposerTier?.visibility || prev.visibility)
    );
  };

  const handleChannelFormChange = (field, value) => {
    setChannelComposerData((prev) => {
      if (field === 'tier') {
        const tierMeta = tierOptions.find((option) => option.id === value);
        if (tierMeta) {
          setChannelComposerTier(tierMeta);
        }
        return { ...prev, tier: value, visibility: prev.visibility || value };
      }
      if (field === 'channel_type') {
        const usingDefaultEmoji = !prev.emoji || prev.emoji === getDefaultEmojiForType(prev.channel_type);
        return {
          ...prev,
          channel_type: value,
          emoji: usingDefaultEmoji ? getDefaultEmojiForType(value) : prev.emoji,
        };
      }
      return { ...prev, [field]: value };
    });
  };

  const handleBotChange = (index, field, value) => {
    setChannelComposerData((prev) => {
      const bots = [...(prev.bots || [])];
      bots[index] = { ...bots[index], [field]: value };
      return { ...prev, bots };
    });
  };

  const handleAddBot = () => {
    setChannelComposerData((prev) => ({
      ...prev,
      bots: [...(prev.bots || []), createEmptyBot()],
    }));
  };

  const handleRemoveBot = (index) => {
    setChannelComposerData((prev) => {
      const bots = [...(prev.bots || [])];
      bots.splice(index, 1);
      return { ...prev, bots };
    });
  };

  const handleChannelSubmit = async (event) => {
    event?.preventDefault?.();
    const trimmedName = channelComposerData.name.trim();
    if (!trimmedName) {
      setChannelComposerError('Channel name is required');
      return;
    }

    setIsSavingChannel(true);
    setChannelComposerError(null);

    const normalizedEmoji = (channelComposerData.emoji || getDefaultEmojiForType(channelComposerData.channel_type)).trim() || getDefaultEmojiForType(channelComposerData.channel_type);
    const botsPayload = (channelComposerData.bots || [])
      .map((bot) => ({
        id: bot.id,
        name: bot.name?.trim() || 'Automation bot',
        trigger: bot.trigger || 'on-join',
        endpoint: bot.endpoint?.trim() || '',
        instructions: bot.instructions?.trim() || '',
      }))
      .filter((bot) => bot.endpoint || bot.instructions || bot.name);

    const metadataPayload = {
      ...(channelComposerData.metadata || {}),
      topic: channelComposerData.topic,
      bots: botsPayload,
    };

    const payload = {
      name: trimmedName,
      channel_type: channelComposerData.channel_type,
      tier: channelComposerData.tier,
      visibility: channelComposerData.visibility || channelComposerData.tier,
      emoji: normalizedEmoji,
      topic: channelComposerData.topic,
      description: channelComposerData.topic,
      metadata: metadataPayload,
      is_default: Boolean(channelComposerData.is_default),
      is_welcome: Boolean(channelComposerData.is_welcome),
      welcome_message: channelComposerData.welcome_message || null,
    };

    if (channelComposerData.slug?.trim()) {
      payload.slug = channelComposerData.slug.trim();
    }

    if (sandboxMode) {
      const nextId = editingChannel?.id || channelComposerData.id || payload.slug || `${payload.tier}-${Date.now()}`;
      const sandboxChannel = {
        id: nextId,
        name: payload.name,
        slug: payload.slug || null,
        emoji: payload.emoji,
        type: payload.channel_type,
        topic: payload.topic,
        visibility: payload.visibility,
        tier: payload.tier,
        metadata: payload.metadata || {},
        is_default: payload.is_default,
        isDefault: payload.is_default,
        is_welcome: payload.is_welcome,
        welcome_message: payload.welcome_message,
        bots: botsPayload,
      };

      setSandboxCommunity((prev) => ({
        ...prev,
        tiers: prev.tiers.map((tier) => {
          let channels = tier.channels.filter((channel) => channel.id !== nextId);
          if (tier.id === payload.tier) {
            const existingIndex = tier.channels.findIndex((channel) => channel.id === nextId);
            if (existingIndex >= 0) {
              channels.splice(existingIndex, 0, sandboxChannel);
            } else {
              channels = [...channels, sandboxChannel];
            }
          }
          return { ...tier, channels };
        }),
      }));

      setChannelComposerOpen(false);
      setEditingChannel(null);
      setComposerBlueprintSource(null);
      setChannelComposerData(buildChannelFormState(null, payload.tier, payload.visibility));
      setIsSavingChannel(false);
      return;
    }

    if (!targetCommunityId) {
      setIsSavingChannel(false);
      setChannelComposerError('Community ID is required');
      return;
    }

    try {
      let response;
      if (editingChannel?.id) {
        response = await apiService.updateChannel(targetCommunityId, editingChannel.id, payload);
      } else {
        response = await apiService.createChannel(targetCommunityId, payload);
      }
      setChannelComposerOpen(false);
      setEditingChannel(null);
      setComposerBlueprintSource(null);
      setChannelComposerData(buildChannelFormState(null, payload.tier, payload.visibility));
      if (typeof onChannelMutation === 'function') {
        await onChannelMutation(response?.channel || null);
      }
    } catch (channelError) {
      console.error('Channel save failed:', channelError);
      setChannelComposerError(channelError.message || 'Unable to save channel');
    } finally {
      setIsSavingChannel(false);
    }
  };

  const handleDeleteChannel = async () => {
    if (!editingChannel?.id) {
      setChannelComposerError('Channel does not exist');
      return;
    }
    setIsDeletingChannel(true);
    setChannelComposerError(null);
    if (sandboxMode) {
      setSandboxCommunity((prev) => ({
        ...prev,
        tiers: prev.tiers.map((tier) => ({
          ...tier,
          channels: tier.channels.filter((channel) => channel.id !== editingChannel.id),
        })),
      }));
      setChannelComposerOpen(false);
      setEditingChannel(null);
      setIsDeletingChannel(false);
      return;
    }
    if (!targetCommunityId) {
      setChannelComposerError('Community ID is required');
      setIsDeletingChannel(false);
      return;
    }
    try {
      await apiService.deleteChannel(targetCommunityId, editingChannel.id);
      setChannelComposerOpen(false);
      setEditingChannel(null);
      if (typeof onChannelMutation === 'function') {
        await onChannelMutation();
      }
    } catch (deleteError) {
      console.error('Channel delete failed:', deleteError);
      setChannelComposerError(deleteError.message || 'Unable to delete channel');
    } finally {
      setIsDeletingChannel(false);
    }
  };

  useEffect(() => {
    setPreviewAs(resolvedPreview);
  }, [resolvedPreview]);

  useEffect(() => {
    setChannelConfigs((prev) => {
      const base = buildChannelMap(blueprintCommunity, layoutOverrides);
      const merged = {};
      Object.keys(base).forEach((channelId) => {
        const previous = prev[channelId];
        merged[channelId] = {
          ...base[channelId],
          ...(previous
            ? {
                name: previous.name,
                topic: previous.topic,
                metadata: previous.metadata,
              }
            : {}),
        };
      });
      return merged;
    });
  }, [blueprintCommunity, layoutOverrides]);

  useEffect(() => {
    if (channelComposerOpen) {
      return;
    }
    const fallbackTier = blueprintCommunity.tiers?.[0] || { id: 'public', label: 'Public Plaza', visibility: 'public' };
    setChannelComposerTier(fallbackTier);
    setChannelComposerData(buildChannelFormState(null, fallbackTier.id, fallbackTier.visibility));
  }, [blueprintCommunity, channelComposerOpen]);

  useEffect(() => {
    let cancelled = false;

    const pullLayout = async () => {
      if (sandboxMode) {
        setLayoutOverrides({});
        setLayoutMeta({ usesCustomLayout: true, updatedAt: null });
        setCustomizeMode(true);
        return;
      }
      if (!community?.id || community.id === defaultCommunityTemplate.id) {
        setLayoutOverrides({});
        setLayoutMeta({ usesCustomLayout: community?.uses_custom_layout ?? false, updatedAt: null });
        return;
      }

      setLayoutLoading(true);
      setLayoutError(null);
      try {
        const response = await apiService.getCommunityLayout(community.id);
        if (cancelled) return;
        const overrides = response?.layout?.channels || response?.channels || {};
        setLayoutOverrides(overrides);
        setLayoutMeta({
          usesCustomLayout: response?.uses_custom_layout ?? community?.uses_custom_layout ?? false,
          updatedAt: response?.layout_updated_at || null,
        });
        if (response?.uses_custom_layout && editable) {
          setCustomizeMode(true);
        }
      } catch (error) {
        if (!cancelled) {
          console.warn('Community layout fetch failed:', error);
          setLayoutOverrides({});
          setLayoutMeta({ usesCustomLayout: community?.uses_custom_layout ?? false, updatedAt: null });
          setLayoutError(error.message || 'Unable to load layout preferences');
        }
      } finally {
        if (!cancelled) {
          setLayoutLoading(false);
        }
      }
    };

    pullLayout();
    return () => {
      cancelled = true;
    };
  }, [community?.id, community?.uses_custom_layout, editable, sandboxMode]);

  const previewVisibilities = useMemo(() => visibilityMatrix[previewAs] || visibilityMatrix.viewer, [previewAs]);
  const effectiveAllowedVisibilities = useMemo(() => {
    const backendAllowed = backendAllowedVisibilities;
    const previewAllowed = previewVisibilities;

    if (backendAllowed === 'all' && previewAllowed === 'all') return 'all';
    if (backendAllowed === 'all') return previewAllowed;
    if (previewAllowed === 'all') return backendAllowed;

    const previewList = Array.isArray(previewAllowed) ? previewAllowed : ['public'];
    const backendList = Array.isArray(backendAllowed) ? backendAllowed : ['public'];
    const intersection = previewList.filter((tier) => backendList.includes(tier));
    return intersection.length ? intersection : ['public'];
  }, [backendAllowedVisibilities, previewVisibilities]);

  const filteredTiers = useMemo(() => {
    if (effectiveAllowedVisibilities === 'all') return blueprintCommunity.tiers;
    return blueprintCommunity.tiers.map((tier) => ({
      ...tier,
      locked: !effectiveAllowedVisibilities.includes(tier.visibility),
    }));
  }, [effectiveAllowedVisibilities, blueprintCommunity]);

  const accessibleChannelCount = useMemo(() => {
    return filteredTiers.reduce((count, tier) => {
      if (tier.locked) return count;
      const tierCount = tier.channels.reduce((inner, channel) => {
        const config = channelConfigs[channel.id];
        if (config?.enabled === false) {
          return inner;
        }
        return inner + 1;
      }, 0);
      return count + tierCount;
    }, 0);
  }, [filteredTiers, channelConfigs]);

  const ingressInfo = useMemo(() => {
    const username = community?.creator_username ? `@${community.creator_username}` : 'this creator';
    return {
      title: community?.ingress?.title || 'Members-only community',
      description:
        community?.ingress?.description || `Follow ${username} or request approval to unlock their private lounges.`,
      actionLabel: community?.ingress?.actionLabel || 'Request access',
      actionUrl:
        community?.ingress?.actionUrl ||
        community?.links?.follow ||
        community?.links?.primary ||
        community?.creator_profile_url ||
        null,
    };
  }, [community]);

  const showIngressPrompt = !sandboxMode && !compact && !editable && accessibleChannelCount === 0;

  const handleChannelChange = (channelId, field, value) => {
    setChannelConfigs((prev) => ({
      ...prev,
      [channelId]: {
        ...prev[channelId],
        [field]: value,
      },
    }));
  };

  const persistCustomizeToggle = async (nextValue) => {
    if (sandboxMode || !community?.id) {
      setLayoutMeta((prev) => ({ ...prev, usesCustomLayout: nextValue }));
      return;
    }

    try {
      await apiService.updateCommunityLayout(community.id, { usesCustomLayout: nextValue });
      setLayoutMeta((prev) => ({ ...prev, usesCustomLayout: nextValue }));
    } catch (error) {
      console.error('Failed to toggle custom layout:', error);
      setLayoutError(error.message || 'Unable to toggle layout');
      setCustomizeMode(!nextValue);
    }
  };

  const handleCustomizeToggle = (checked) => {
    if (!editable) return;
    setCustomizeMode(checked);
    persistCustomizeToggle(checked);
  };

  const handleSaveLayout = async () => {
    if (!editable) return;
    if (sandboxMode || !community?.id) {
      setLayoutMeta((prev) => ({ ...prev, usesCustomLayout: true, updatedAt: new Date().toISOString() }));
      return;
    }
    setIsSavingLayout(true);
    setLayoutError(null);

    const channelPayload = Object.entries(channelConfigs).reduce((acc, [channelId, config]) => {
      acc[channelId] = {
        enabled: config.enabled !== false,
        priority: Number(config.priority) || 0,
      };
      return acc;
    }, {});

    try {
      const response = await apiService.updateCommunityLayout(community.id, {
        usesCustomLayout: true,
        layout: { channels: channelPayload },
      });
      const savedChannels = response?.layout?.layout?.channels || channelPayload;
      setLayoutMeta({
        usesCustomLayout: true,
        updatedAt: response?.layout?.layout_updated_at || new Date().toISOString(),
      });
      setLayoutOverrides(savedChannels);
      setCustomizeMode(true);
    } catch (error) {
      console.error('Failed to save layout:', error);
      setLayoutError(error.message || 'Saving layout failed');
    } finally {
      setIsSavingLayout(false);
    }
  };

  const titleSuffix = community?.creator_username ? ` · @${community.creator_username}` : '';
  const applyButtonDisabled = !targetCommunityId;
  const canBuildChannel = editable && (sandboxMode || !applyButtonDisabled);
  const isMobile = useMediaQuery('(max-width: 768px)');
  const actionBarOffset = typeof window !== 'undefined' && window?.innerHeight < 700 ? 'bottom-24' : 'bottom-20';
  const applyCtaDisabled = isScaffolding || layoutLoading || applyButtonDisabled;
  const applyButtonLabel = isScaffolding ? 'Applying…' : 'Apply blueprint to community';
  const renderApplyButton = (className) => {
    if (sandboxMode) return null;
    return (
      <button
        type="button"
        onClick={handleScaffoldChannels}
        disabled={applyCtaDisabled}
        className={className}
      >
        {applyButtonLabel}
      </button>
    );
  };

  const handleAdoptBlueprintChannel = (tier, channel, event) => {
    if (!canBuildChannel || tier?.locked) return;
    event?.stopPropagation?.();
    openChannelComposer(
      tier,
      {
        ...channel,
        id: null,
        tier: tier.id,
        visibility: channel.visibility || tier.visibility || tier.id,
      },
      {
        forceCreate: true,
        blueprintSource: `${tier.label} · ${channel.name}`,
      }
    );
  };

  return (
    <>
      <div className="flex flex-col gap-6">
      {!compact && (
        <div className="flex flex-col gap-4 rounded-3xl border border-white/10 bg-gradient-to-br from-white/5 to-white/0 p-8">
          <div className={isMobile ? 'flex flex-col gap-6' : 'flex flex-wrap items-center justify-between gap-4'}>
            <div>
              <p className="text-sm uppercase tracking-[0.3em] text-white/50">{sandboxMode ? 'Drafting' : 'Community Blueprint'}</p>
              <h1 className="text-3xl font-black text-white">
                {blueprintCommunity.name}
                {titleSuffix}
              </h1>
              <p className="mt-2 text-slate-300 max-w-2xl">{blueprintCommunity.description}</p>
            </div>
            {!sandboxMode && (
              <div
                className={`flex items-center gap-3 bg-white/5 border border-white/10 rounded-full px-5 py-2 ${isMobile ? 'w-full justify-between' : ''}`}
              >
                <span className="text-sm text-white/60">Preview As</span>
                <select
                  className="bg-transparent text-white text-sm focus:outline-none"
                  value={previewAs}
                  onChange={(e) => {
                    const next = e.target.value;
                    setPreviewAs(next);
                    if (next !== 'owner' && next !== 'staff') {
                      setCustomizeMode(false);
                    }
                  }}
                >
                  {previewModes.map((mode) => (
                    <option key={mode.id} value={mode.id} className="bg-slate-900">
                      {mode.label}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>

          <div className={isMobile ? 'flex flex-col gap-4' : 'flex flex-wrap items-center justify-between gap-4'}>
            <div className="flex flex-wrap gap-3 text-xs text-slate-300">
              <span className="inline-flex items-center gap-2 rounded-full border border-white/10 px-3 py-1">
                <span className="h-2 w-2 rounded-full bg-emerald-400" /> text channel
              </span>
              <span className="inline-flex items-center gap-2 rounded-full border border-white/10 px-3 py-1">
                <span className="h-2 w-2 rounded-full bg-sky-400" /> voice room
              </span>
              <span className="inline-flex items-center gap-2 rounded-full border border-white/10 px-3 py-1">
                <span role="img" aria-hidden="true">🔒</span> gated tier
              </span>
            </div>
            <div className={isMobile ? 'flex flex-col gap-3 w-full' : 'flex flex-wrap items-center gap-3'}>
              {!sandboxMode && (
                <label className={`flex items-center gap-2 text-sm ${editable ? 'text-white' : 'text-white/30'}`}>
                  <input
                    type="checkbox"
                    className="accent-emerald-400"
                    disabled={!editable}
                    checked={customizeMode && editable}
                    onChange={(e) => handleCustomizeToggle(e.target.checked)}
                  />
                  Customize layout
                </label>
              )}
              {editable && (
                <button
                  type="button"
                  onClick={() => openChannelComposer(defaultComposerTier, null, { forceCreate: true })}
                  disabled={!canBuildChannel}
                  className={`${isMobile ? 'w-full text-center' : ''} rounded-full border border-white/10 px-4 py-2 text-sm text-white/80 hover:bg-white/10 disabled:opacity-50`}
                >
                  + Build custom channel
                </button>
              )}
              {editable && !sandboxMode && (
                <div className={isMobile ? 'flex flex-col gap-2 w-full' : 'flex flex-wrap gap-2'}>
                  {customizeMode && (
                    <button
                      type="button"
                      onClick={handleSaveLayout}
                      disabled={isSavingLayout || layoutLoading}
                      className="rounded-full border border-purple-400/40 px-4 py-2 text-sm text-white/80 hover:bg-purple-500/20 disabled:opacity-50"
                    >
                      {isSavingLayout ? 'Saving…' : 'Save layout'}
                    </button>
                  )}
                  {renderApplyButton(
                    `${isMobile ? 'w-full text-center' : ''} rounded-full border border-emerald-400/40 px-4 py-2 text-sm text-emerald-100 hover:bg-emerald-500/20 disabled:opacity-50`
                  )}
                  {applyButtonDisabled && scaffoldDisabledReason && (
                    <p className={`${isMobile ? 'text-left' : 'text-right'} w-full text-xs text-white/60`}>{scaffoldDisabledReason}</p>
                  )}
                </div>
              )}
            </div>
          </div>
          {layoutError && (
            <div className="rounded-2xl border border-red-500/30 bg-red-500/10 px-4 py-2 text-xs text-red-200">
              {layoutError}
            </div>
          )}
          {channelsLoading && (
            <p className="text-xs text-white/50">Refreshing live channels…</p>
          )}
          {!layoutError && layoutMeta.updatedAt && customizeMode && (
            <p className="text-xs text-white/40">Last saved {new Date(layoutMeta.updatedAt).toLocaleString()}</p>
          )}
          {!sandboxMode && scaffoldResult?.tests && scaffoldResult.tests.length > 0 && (
            <div className="rounded-2xl border border-emerald-400/30 bg-emerald-500/10 px-4 py-3 text-xs text-emerald-100">
              <p className="font-semibold text-emerald-50">
                Verified {scaffoldResult.totalChannels ?? scaffoldResult.channels?.length ?? 0} channels.
              </p>
              <div className="mt-2 space-y-1 max-h-32 overflow-y-auto custom-scrollbar">
                {scaffoldResult.tests.slice(0, 6).map((test) => (
                  <p key={`${test.channelId}-${test.key}`} className={test.passed ? 'text-emerald-100' : 'text-amber-100'}>
                    #{test.blueprintId || test.channelId} · {test.key}: {test.passed ? 'pass' : 'check'}
                  </p>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {showIngressPrompt && (
        <div className="rounded-3xl border border-amber-400/20 bg-amber-500/10 p-6 text-white/90">
          <div className="flex flex-col gap-3">
            <div>
              <p className="text-xs uppercase tracking-[0.3em] text-amber-200/80">Community ingress</p>
              <h3 className="mt-1 text-2xl font-semibold text-white">{ingressInfo.title}</h3>
              <p className="text-sm text-amber-100/90">{ingressInfo.description}</p>
            </div>
            <div className="flex flex-wrap gap-3">
              {typeof onIngressRequest === 'function' ? (
                <button
                  type="button"
                  onClick={() => onIngressRequest(ingressInfo)}
                  className="rounded-full border border-white/20 px-5 py-2 text-sm font-semibold text-white hover:bg-white/10"
                >
                  {ingressInfo.actionLabel}
                </button>
              ) : ingressInfo.actionUrl ? (
                <a
                  href={ingressInfo.actionUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-2 rounded-full border border-white/20 px-5 py-2 text-sm font-semibold text-white hover:bg-white/10"
                >
                  {ingressInfo.actionLabel}
                  <span aria-hidden="true">↗</span>
                </a>
              ) : (
                <span className="text-xs text-white/60">Contact the creator to request access.</span>
              )}
            </div>
          </div>
        </div>
      )}

      <div className={`grid gap-6 ${compact ? 'gap-4' : ''}`}>
        {filteredTiers.map((tier) => {
          const tierLocked = Boolean(tier.locked) && !sandboxMode;
          return (
            <section
              key={tier.id}
              className={`rounded-3xl border ${tierLocked ? 'border-white/5 bg-white/[0.02]' : 'border-white/10 bg-white/5'} ${compact ? 'p-4' : 'p-6'}`}
            >
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div>
                  <div
                    className={`inline-flex items-center gap-2 rounded-full bg-gradient-to-r ${tier.badgeColor} px-3 py-1 text-xs font-semibold text-white shadow-lg`}
                  >
                    {tier.label}
                    {tierLocked && <span role="img" aria-hidden="true">🔒</span>}
                  </div>
                  {!compact && (
                    <>
                      <p className="mt-2 text-lg font-semibold text-white">{tier.description}</p>
                      <p className="text-sm text-slate-400">{tier.requirementLabel}</p>
                    </>
                  )}
                </div>
                {!tierLocked && editable && !compact && customizeMode && (
                  <button
                    type="button"
                    onClick={() => openChannelComposer(tier)}
                    disabled={layoutLoading}
                    className="rounded-full border border-white/20 px-4 py-2 text-sm text-white/80 hover:bg-white/10 disabled:opacity-50"
                  >
                    + Add channel
                  </button>
                )}
              </div>

              <div className={`mt-6 grid gap-4 ${compact ? 'grid-cols-1' : 'md:grid-cols-2'}`}>
                {tier.channels.map((channel) => {
                  const config = channelConfigs[channel.id] || channel;
                  const typeBadge = channelTypeBadge[channel.type] || channelTypeBadge.text;
                  const isSelected = selectedChannelId === channel.id;
                  const isEnabled = config.enabled !== false;
                  const botCount = Array.isArray(config.metadata?.bots)
                    ? config.metadata.bots.length
                    : Array.isArray(config.bots)
                    ? config.bots.length
                    : 0;
                  const hasBots = botCount > 0;
                  
                  return (
                    <div
                      key={channel.id}
                      role="button"
                      tabIndex={0}
                      onClick={() => onChannelSelect?.(channel)}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter') {
                          onChannelSelect?.(channel);
                        }
                      }}
                      className={`rounded-2xl border p-4 focus:outline-none focus:ring-2 focus:ring-white/30 transition-all ${
                        isSelected 
                          ? 'border-purple-500 bg-purple-500/20' 
                          : 'border-white/10 bg-black/30 hover:bg-white/5'
                      } ${
                        tierLocked ? 'opacity-60 cursor-not-allowed' : 'opacity-100'
                      } ${
                        !isEnabled && customizeMode ? 'opacity-40 ring-0 border-dashed' : ''
                      }`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex items-center gap-3">
                          <div className="text-2xl">{config.emoji}</div>
                          <div>
                            {customizeMode && editable && !compact ? (
                              <input
                                className="bg-transparent text-lg font-semibold text-white focus:outline-none border-b border-white/20"
                                value={config.name || ''}
                                onChange={(e) => handleChannelChange(channel.id, 'name', e.target.value)}
                              />
                            ) : (
                              <p className={`font-semibold text-white ${compact ? 'text-sm' : 'text-lg'}`}>{config.name}</p>
                            )}
                            {!compact && (
                              customizeMode && editable ? (
                                <input
                                  className="mt-1 w-full bg-transparent text-sm text-slate-300 focus:outline-none border-b border-white/10"
                                  value={config.topic || ''}
                                  onChange={(e) => handleChannelChange(channel.id, 'topic', e.target.value)}
                                />
                              ) : (
                                <p className="text-sm text-slate-300">{config.topic}</p>
                              )
                            )}
                          </div>
                        </div>
                        {!compact && (
                          <div className="flex flex-wrap items-center gap-2 justify-end">
                            <span className={`rounded-full px-3 py-1 text-xs font-semibold ${typeBadge.classes}`}>
                              {typeBadge.label}
                            </span>
                            {hasBots && (
                              <span className="inline-flex items-center gap-1 rounded-full border border-cyan-400/40 px-3 py-1 text-xs font-semibold text-cyan-100">
                                <span role="img" aria-hidden="true">🤖</span>
                                {botCount} bot{botCount === 1 ? '' : 's'}
                              </span>
                            )}
                            {customizeMode && editable && !tierLocked && (
                              <button
                                type="button"
                                onClick={(event) => {
                                  event.stopPropagation();
                                  openChannelComposer(tier, channel);
                                }}
                                className="text-xs font-semibold text-white/70 hover:text-white border border-white/20 rounded-full px-2 py-1"
                              >
                                Edit
                              </button>
                            )}
                            {editable && !tierLocked && !sandboxMode && (
                              <button
                                type="button"
                                onClick={(event) => handleAdoptBlueprintChannel(tier, channel, event)}
                                disabled={!canBuildChannel}
                                className="text-xs font-semibold text-emerald-200 hover:text-emerald-50 border border-emerald-400/30 rounded-full px-3 py-1 disabled:opacity-40"
                              >
                                Add to my community
                              </button>
                            )}
                          </div>
                        )}
                      </div>

                      {!tierLocked && !compact && (
                        <div className="mt-4 flex flex-wrap gap-2 text-xs text-slate-400">
                          <span className="inline-flex items-center gap-1 rounded-full border border-white/10 px-2 py-1">
                            <span role="img" aria-hidden="true">🤝</span>
                            {tier.visibility}
                          </span>
                          {customizeMode && editable && (
                            <span className="inline-flex cursor-pointer items-center gap-1 rounded-full border border-white/10 px-2 py-1 text-white/80">
                              <span role="img" aria-hidden="true">⚙️</span>
                              Configure permissions
                            </span>
                          )}
                        </div>
                      )}

                      {customizeMode && editable && !compact && (
                        <div className="mt-4 flex flex-wrap gap-4 text-sm text-white/80">
                          <label className="inline-flex items-center gap-2">
                            <input
                              type="checkbox"
                              className="accent-purple-400"
                              checked={config.enabled !== false}
                              onChange={(e) => handleChannelChange(channel.id, 'enabled', e.target.checked)}
                            />
                            Show on profile
                          </label>
                          <label className="inline-flex items-center gap-2">
                            Priority
                            <input
                              type="number"
                              min="0"
                              max="999"
                              value={config.priority ?? ''}
                              onChange={(e) => handleChannelChange(channel.id, 'priority', Number(e.target.value) || 0)}
                              className="w-20 rounded-lg border border-white/10 bg-black/40 px-2 py-1 text-white focus:outline-none"
                            />
                          </label>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </section>
          );
        })}
      </div>
      </div>

      {editable && !compact && isMobile && (
        <div className={`md:hidden fixed ${actionBarOffset} inset-x-0 z-40 px-4`}>
          <div className="rounded-3xl border border-white/15 bg-slate-950/95 backdrop-blur px-5 py-4 shadow-2xl shadow-black/40 flex flex-col gap-4">
            <div className="flex flex-col gap-3">
              <label className={`flex items-center justify-between gap-4 text-sm ${editable ? 'text-white' : 'text-white/30'}`}>
                <span className="font-semibold">Customize layout</span>
                <input
                  type="checkbox"
                  className="accent-emerald-400"
                  disabled={!editable}
                  checked={customizeMode && editable}
                  onChange={(e) => handleCustomizeToggle(e.target.checked)}
                />
              </label>
              {canBuildChannel && (
                <button
                  type="button"
                  onClick={() => openChannelComposer(defaultComposerTier, null, { forceCreate: true })}
                  className="w-full rounded-2xl border border-white/10 px-4 py-2 text-sm font-semibold text-white/80 hover:bg-white/10"
                >
                  + Build custom channel
                </button>
              )}
            </div>
            {renderApplyButton('w-full rounded-2xl border border-emerald-400/40 bg-emerald-500/15 px-4 py-3 text-base font-semibold text-emerald-50 shadow-inner disabled:opacity-60')}
            {!sandboxMode && applyButtonDisabled && scaffoldDisabledReason && (
              <p className="text-center text-xs text-white/60">{scaffoldDisabledReason}</p>
            )}
          </div>
        </div>
      )}

      {channelComposerOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4 backdrop-blur">
          <div className="w-full max-w-2xl rounded-3xl border border-white/10 bg-slate-950/95 p-6 shadow-2xl">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-xs uppercase tracking-[0.3em] text-white/40">
                  {editingChannel ? 'Edit channel' : 'Create channel'}
                </p>
                <h3 className="mt-1 text-2xl font-semibold text-white">
                  {channelComposerTier?.label || 'Channel'}
                </h3>
                <p className="text-sm text-white/50">Configure tier access, format, and onboarding copy.</p>
                {composerBlueprintSource && (
                  <div className="mt-3 inline-flex items-center gap-2 rounded-full border border-sky-500/30 bg-sky-500/10 px-3 py-1 text-xs text-sky-100">
                    <span role="img" aria-hidden="true">✨</span>
                    Based on blueprint: {composerBlueprintSource}
                  </div>
                )}
              </div>
              <button
                type="button"
                onClick={closeChannelComposer}
                className="text-white/60 hover:text-white"
                aria-label="Close channel composer"
              >
                ✕
              </button>
            </div>

            <form className="mt-6 space-y-5" onSubmit={handleChannelSubmit}>
              <div className="grid gap-4 md:grid-cols-2">
                <label className="flex flex-col gap-2 text-sm text-white/70">
                  Channel name
                  <input
                    type="text"
                    value={channelComposerData.name}
                    onChange={(e) => handleChannelFormChange('name', e.target.value)}
                    className="rounded-2xl border border-white/10 bg-white/5 px-3 py-2 text-white focus:border-white/40 focus:outline-none"
                    placeholder="Town hall, lounge, war room"
                  />
                </label>
                <label className="flex flex-col gap-2 text-sm text-white/70">
                  Slug (optional)
                  <input
                    type="text"
                    value={channelComposerData.slug}
                    onChange={(e) => handleChannelFormChange('slug', e.target.value)}
                    className="rounded-2xl border border-white/10 bg-white/5 px-3 py-2 text-white focus:border-white/40 focus:outline-none"
                    placeholder="auto-generated when empty"
                  />
                  <span className="text-xs text-white/40">URL-friendly identifier. Leave blank to auto-generate.</span>
                </label>
              </div>

              <div className="grid gap-4 md:grid-cols-3">
                <label className="flex flex-col gap-2 text-sm text-white/70">
                  Tier visibility
                  <select
                    value={channelComposerData.tier}
                    onChange={(e) => handleChannelFormChange('tier', e.target.value)}
                    className="rounded-2xl border border-white/10 bg-white/5 px-3 py-2 text-white focus:border-white/40 focus:outline-none"
                  >
                    {tierOptions.map((tier) => (
                      <option key={tier.id} value={tier.id} className="bg-slate-900">
                        {tier.label}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="flex flex-col gap-2 text-sm text-white/70">
                  Channel type
                  <select
                    value={channelComposerData.channel_type}
                    onChange={(e) => handleChannelFormChange('channel_type', e.target.value)}
                    className="rounded-2xl border border-white/10 bg-white/5 px-3 py-2 text-white focus:border-white/40 focus:outline-none"
                  >
                    {channelTypeOptions.map((type) => (
                      <option key={type.id} value={type.id} className="bg-slate-900">
                        {type.label}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="flex flex-col gap-2 text-sm text-white/70">
                  Emoji / icon
                  <input
                    type="text"
                    value={channelComposerData.emoji}
                    onChange={(e) => handleChannelFormChange('emoji', e.target.value)}
                    className="rounded-2xl border border-white/10 bg-white/5 px-3 py-2 text-white focus:border-white/40 focus:outline-none"
                    placeholder="💬"
                    maxLength={4}
                  />
                </label>
              </div>

              <label className="flex flex-col gap-2 text-sm text-white/70">
                Topic / onboarding copy
                <textarea
                  value={channelComposerData.topic}
                  onChange={(e) => handleChannelFormChange('topic', e.target.value)}
                  rows={3}
                  className="rounded-2xl border border-white/10 bg-white/5 px-3 py-2 text-white focus:border-white/40 focus:outline-none"
                  placeholder="What happens inside this room?"
                />
              </label>

              <div className="grid gap-4 md:grid-cols-2">
                <label className="flex flex-col gap-2 text-sm text-white/70">
                  Welcome message (optional)
                  <textarea
                    value={channelComposerData.welcome_message}
                    onChange={(e) => handleChannelFormChange('welcome_message', e.target.value)}
                    rows={3}
                    className="rounded-2xl border border-white/10 bg-white/5 px-3 py-2 text-white focus:border-white/40 focus:outline-none"
                    placeholder="Drop context for new arrivals"
                  />
                </label>
                <div className="flex flex-col gap-4 rounded-2xl border border-white/10 bg-white/5 p-4 text-sm text-white/80">
                  <label className="inline-flex items-center gap-2">
                    <input
                      type="checkbox"
                      className="accent-emerald-400"
                      checked={channelComposerData.is_default}
                      onChange={(e) => handleChannelFormChange('is_default', e.target.checked)}
                    />
                    Show by default for this tier
                  </label>
                  <label className="inline-flex items-center gap-2">
                    <input
                      type="checkbox"
                      className="accent-purple-400"
                      checked={channelComposerData.is_welcome}
                      onChange={(e) => handleChannelFormChange('is_welcome', e.target.checked)}
                    />
                    Mark as welcome / onboarding room
                  </label>
                </div>
              </div>

              <div className="rounded-3xl border border-white/10 bg-white/5 p-4 text-sm text-white/80 space-y-4">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <p className="text-xs uppercase tracking-[0.3em] text-white/40">Automation</p>
                    <h4 className="font-semibold text-white">Bots & triggers</h4>
                    <p className="text-xs text-white/60">Send webhooks, scripted welcomes, or scheduled drops.</p>
                  </div>
                  <button
                    type="button"
                    onClick={handleAddBot}
                    className="rounded-full border border-white/15 px-3 py-1 text-xs font-semibold text-white/80 hover:bg-white/10"
                  >
                    + Add bot
                  </button>
                </div>

                {(channelComposerData.bots?.length || 0) === 0 && (
                  <p className="rounded-2xl border border-dashed border-white/15 bg-black/20 px-4 py-3 text-xs text-white/60">
                    No automations yet. Use “Add bot” to attach a webhook or scripted action to this room.
                  </p>
                )}

                <div className="space-y-4">
                  {(channelComposerData.bots || []).map((bot, index) => (
                    <div key={bot.id || index} className="rounded-2xl border border-white/15 bg-black/30 p-4 space-y-3">
                      <div className="flex items-center justify-between">
                        <p className="text-xs uppercase tracking-[0.2em] text-white/50">Bot #{index + 1}</p>
                        <button
                          type="button"
                          onClick={() => handleRemoveBot(index)}
                          className="text-xs text-white/50 hover:text-white"
                        >
                          Remove
                        </button>
                      </div>
                      <div className="grid gap-3 md:grid-cols-2">
                        <label className="flex flex-col gap-1 text-xs text-white/60">
                          Bot name
                          <input
                            type="text"
                            value={bot.name}
                            onChange={(e) => handleBotChange(index, 'name', e.target.value)}
                            className="rounded-xl border border-white/10 bg-white/5 px-3 py-2 text-white focus:border-white/40 focus:outline-none"
                            placeholder="Welcome steward"
                          />
                        </label>
                        <label className="flex flex-col gap-1 text-xs text-white/60">
                          Trigger
                          <select
                            value={bot.trigger}
                            onChange={(e) => handleBotChange(index, 'trigger', e.target.value)}
                            className="rounded-xl border border-white/10 bg-white/5 px-3 py-2 text-white focus:border-white/40 focus:outline-none"
                          >
                            {botTriggerOptions.map((option) => (
                              <option key={option.id} value={option.id} className="bg-slate-900">
                                {option.label}
                              </option>
                            ))}
                          </select>
                        </label>
                      </div>
                      <label className="flex flex-col gap-1 text-xs text-white/60">
                        Webhook / endpoint URL
                        <input
                          type="url"
                          value={bot.endpoint}
                          onChange={(e) => handleBotChange(index, 'endpoint', e.target.value)}
                          className="rounded-xl border border-white/10 bg-white/5 px-3 py-2 text-white focus:border-white/40 focus:outline-none"
                          placeholder="https://hooks.myservice.com/..."
                        />
                      </label>
                      <label className="flex flex-col gap-1 text-xs text-white/60">
                        Instructions / payload
                        <textarea
                          value={bot.instructions}
                          onChange={(e) => handleBotChange(index, 'instructions', e.target.value)}
                          rows={2}
                          className="rounded-xl border border-white/10 bg-white/5 px-3 py-2 text-white focus:border-white/40 focus:outline-none"
                          placeholder="JSON payload or script notes"
                        />
                      </label>
                    </div>
                  ))}
                </div>
              </div>

              {channelComposerError && (
                <div className="rounded-2xl border border-red-500/40 bg-red-500/10 px-4 py-2 text-sm text-red-100">
                  {channelComposerError}
                </div>
              )}

              <div className="flex flex-wrap items-center justify-between gap-3">
                {editingChannel && (
                  <button
                    type="button"
                    onClick={handleDeleteChannel}
                    disabled={isDeletingChannel || isSavingChannel}
                    className="text-sm text-red-200 hover:text-red-100 disabled:opacity-40"
                  >
                    {isDeletingChannel ? 'Deleting…' : 'Delete channel'}
                  </button>
                )}
                <div className="ml-auto flex gap-3">
                  <button
                    type="button"
                    onClick={closeChannelComposer}
                    disabled={isSavingChannel}
                    className="rounded-full border border-white/20 px-4 py-2 text-sm text-white/80 hover:bg-white/10 disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isSavingChannel || isDeletingChannel}
                    className="rounded-full border border-emerald-400/40 bg-emerald-500/20 px-4 py-2 text-sm font-semibold text-emerald-100 hover:bg-emerald-500/30 disabled:opacity-50"
                  >
                    {isSavingChannel ? 'Saving…' : editingChannel ? 'Update channel' : 'Create channel'}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}

export default CommunityBlueprint;
